import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import * as Sentry from "@sentry/react";
import { SecureStoragePlugin } from "capacitor-secure-storage-plugin";

// Android caveat (MEIS-13228): `capacitor-secure-storage-plugin` does NOT
// fail closed. `PasswordStorageHelper`'s constructor silently falls back to
// `PasswordStorageHelper_SDK16` whenever the AndroidKeyStore-backed
// implementation fails to initialise — on any API level, not just <18 — and
// that fallback only Base64-encodes the value into SharedPreferences. So on a
// device where Keystore init fails, tokens are stored plaintext-equivalent
// rather than Keystore-encrypted, with no signal to the caller. iOS (Keychain)
// is unaffected. Treat the Android guarantee as best-effort until the plugin is
// replaced with a fail-closed one.
const STORAGE_KEY = "keycloak-tokens";

// The native plugin rejects `get` and `remove` with this exact message when
// the key is absent (Android `SecureStoragePluginPlugin.java`, iOS
// `SecureStoragePlugin.swift`). The message is only used to decide whether a
// rejection is worth reporting to Sentry — behaviour must never hinge on it:
// if the plugin ever rewords the error, `read` still has to fall through to
// the legacy-migration path, or existing users get signed out on upgrade.
const NOT_EXIST_MESSAGE = "Item with given key does not exist";

export interface StoredKeycloakTokens {
  token: string;
  refreshToken: string;
  idToken?: string;
}

function isStoredKeycloakTokens(value: unknown): value is StoredKeycloakTokens {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  if (typeof v.token !== "string" || v.token.length === 0) return false;
  if (typeof v.refreshToken !== "string" || v.refreshToken.length === 0)
    return false;
  return v.idToken === undefined || typeof v.idToken === "string";
}

function isNotExistError(e: unknown): boolean {
  return e instanceof Error && e.message === NOT_EXIST_MESSAGE;
}

function parseStoredTokens(raw: string): StoredKeycloakTokens | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (e) {
    Sentry.captureException(e);
    return null;
  }
  return isStoredKeycloakTokens(parsed) ? parsed : null;
}

async function readFromSecureStorage(): Promise<
  | { hit: true; tokens: StoredKeycloakTokens | null }
  | { hit: false; confirmedAbsent: boolean }
> {
  try {
    const { value } = await SecureStoragePlugin.get({ key: STORAGE_KEY });
    if (!value) return { hit: true, tokens: null };
    return { hit: true, tokens: parseStoredTokens(value) };
  } catch (e) {
    // Any rejection — expected "not exist" or a real plugin failure — is
    // treated as a miss so `read` falls through to the legacy-migration
    // path. Treating an unexpected rejection as a hit-with-null would skip
    // the migration and sign existing users out on upgrade. Only the
    // expected miss stays silent; anything else is reported.
    const confirmedAbsent = isNotExistError(e);
    if (!confirmedAbsent) Sentry.captureException(e);
    return { hit: false, confirmedAbsent };
  }
}

// TODO(MEIS-12366): remove this migration helper and its call site in `read`
// no earlier than three months after the Capacitor 8 store release, by which
// point all active users should have launched the new build once and migrated
// their tokens from @capacitor/preferences to SecureStoragePlugin. The window
// deliberately tracks the store release rather than a fixed date: removing it
// too early would sign out anyone updating later and leave their legacy
// plaintext token behind.
async function migrateFromPreferences({
  persist,
}: {
  persist: boolean;
}): Promise<StoredKeycloakTokens | null> {
  let raw: string | null;
  try {
    const result = await Preferences.get({ key: STORAGE_KEY });
    raw = result.value;
  } catch (e) {
    Sentry.captureException(e);
    return null;
  }
  if (!raw) return null;

  const tokens = parseStoredTokens(raw);
  if (!tokens) return null;

  // Only copy into secure storage when the plugin confirmed the key is
  // absent. After an unexpected read failure secure storage may already hold
  // newer tokens (e.g. a successful write whose legacy cleanup failed), and
  // copying the legacy value would overwrite them with stale ones. The user
  // stays signed in with the legacy tokens either way; the next successful
  // `write()` persists fresh tokens and removes the legacy copy.
  if (!persist) return tokens;

  // Copy first, then remove the plaintext copy. If the secure write fails the
  // legacy value stays in place so the next launch can retry the migration.
  try {
    await SecureStoragePlugin.set({
      key: STORAGE_KEY,
      value: JSON.stringify(tokens),
    });
  } catch (e) {
    Sentry.captureException(e);
    return tokens;
  }
  try {
    await Preferences.remove({ key: STORAGE_KEY });
  } catch (e) {
    // The secure copy exists but the plaintext one could not be deleted.
    // Left as-is, every later read() would hit the secure value and skip
    // this migration path forever, so the plaintext tokens would linger
    // indefinitely. Roll the secure entry back so the next launch retries
    // the whole migration (set + remove) until the legacy copy is gone.
    Sentry.captureException(e);
    try {
      await SecureStoragePlugin.remove({ key: STORAGE_KEY });
    } catch (rollbackError) {
      Sentry.captureException(rollbackError);
    }
  }
  return tokens;
}

// CAP6-COMPAT(MEIS-13783): old Capacitor 6 store builds (iOS <= 84 / Android <= 75) ship without the SecureStoragePlugin native code, so the OTA-delivered bundle keeps the tokens in the legacy `@capacitor/preferences` key there; the key stays intact so the migration runs once the Cap 8 build is installed. Remove once the Cap 6 store builds (iOS <= 84 / Android <= 75) are gone.
function isSecureStorageUnavailable(): boolean {
  return !Capacitor.isPluginAvailable("SecureStoragePlugin");
}

// CAP6-COMPAT(MEIS-13783): legacy Preferences-only storage used when SecureStoragePlugin is missing natively. Remove once the Cap 6 store builds (iOS <= 84 / Android <= 75) are gone.
const legacyPreferencesTokenStorage = {
  async read(): Promise<StoredKeycloakTokens | null> {
    let raw: string | null;
    try {
      raw = (await Preferences.get({ key: STORAGE_KEY })).value;
    } catch (e) {
      Sentry.captureException(e);
      return null;
    }
    return raw ? parseStoredTokens(raw) : null;
  },

  async write(tokens: StoredKeycloakTokens): Promise<void> {
    try {
      await Preferences.set({
        key: STORAGE_KEY,
        value: JSON.stringify(tokens),
      });
    } catch (e) {
      Sentry.captureException(e);
    }
  },

  async clear(): Promise<void> {
    try {
      await Preferences.remove({ key: STORAGE_KEY });
    } catch (e) {
      Sentry.captureException(e);
    }
  },
};

export const keycloakCapacitorTokenStorage = {
  async read(): Promise<StoredKeycloakTokens | null> {
    // CAP6-COMPAT(MEIS-13783): Preferences-only path for shells without SecureStoragePlugin. Remove once the Cap 6 store builds (iOS <= 84 / Android <= 75) are gone.
    if (isSecureStorageUnavailable())
      return legacyPreferencesTokenStorage.read();
    const secure = await readFromSecureStorage();
    if (secure.hit) return secure.tokens;
    return migrateFromPreferences({ persist: secure.confirmedAbsent });
  },

  async write(tokens: StoredKeycloakTokens): Promise<void> {
    // CAP6-COMPAT(MEIS-13783): Preferences-only path for shells without SecureStoragePlugin. Remove once the Cap 6 store builds (iOS <= 84 / Android <= 75) are gone.
    if (isSecureStorageUnavailable()) {
      return legacyPreferencesTokenStorage.write(tokens);
    }
    try {
      await SecureStoragePlugin.set({
        key: STORAGE_KEY,
        value: JSON.stringify(tokens),
      });
    } catch (e) {
      // The legacy Preferences copy (if any) is left alone, so a failed write
      // never loses the user's only stored tokens.
      Sentry.captureException(e);
      return;
    }
    // Once secure storage holds the current tokens, every later read() hits it
    // and never reaches the migration path again. A migration whose secure
    // write failed transiently returned the legacy tokens without deleting
    // them, so the plaintext copy would otherwise be stranded. Remove it on
    // every successful write; a failed removal is retried by the next one.
    try {
      await Preferences.remove({ key: STORAGE_KEY });
    } catch (e) {
      Sentry.captureException(e);
    }
  },

  async clear(): Promise<void> {
    // CAP6-COMPAT(MEIS-13783): Preferences-only path for shells without SecureStoragePlugin. Remove once the Cap 6 store builds (iOS <= 84 / Android <= 75) are gone.
    if (isSecureStorageUnavailable())
      return legacyPreferencesTokenStorage.clear();
    // A secure copy that survives logout would be read back on the next cold
    // start and silently restore the session, so a failed removal is retried
    // once. Logout itself must never hang or throw: a second real failure is
    // only reported. This narrows the window; the fail-closed storage plugin
    // (MEIS-13228) closes it.
    for (let attempt = 1; attempt <= 2; attempt += 1) {
      try {
        await SecureStoragePlugin.remove({ key: STORAGE_KEY });
        break;
      } catch (e) {
        if (isNotExistError(e)) break;
        if (attempt === 2) Sentry.captureException(e);
      }
    }
    // Defence-in-depth: a user who logs out before their first migration-aware
    // read would otherwise leave plaintext tokens in `@capacitor/preferences`.
    try {
      await Preferences.remove({ key: STORAGE_KEY });
    } catch (e) {
      Sentry.captureException(e);
    }
  },
};
